import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit2,
  Trash2,
  FileText,
  Calendar,
  DollarSign,
  Link2,
  Package,
  ChevronDown
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';
import { formatDateToISO } from '../../../utils/formatters';
import {
  getExpenses,
  approveExpense,
  rejectExpense,
  deleteExpense,
  postExpenseToGL
} from '../../../api/expenses';
import { getExpenseCategories } from '../../../api/expenseCategories';
import ExpenseFormModal from '../../../components/ExpenseFormModal';
import ExpenseDetailModal from './ExpenseDetailModal';
import ExpenseReportModal from './ExpenseReportModal';
import Pagination from '../../../components/common/Pagination';
import PeriodFilter from '../../../components/common/PeriodFilter';

const WholesaleExpensesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    category_id: '',
    search: '',
    month: '',
    year: ''
  });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const isAdmin =
    user?.role === 'admin' ||
    user?.role === 'super_admin' ||
    user?.role === 'wholesale_admin' ||
    user?.role === 'system_admin';

  // Fiscal period: global + local override
  const { periodRange, settings } = useFiscalPeriod();
  const [localPeriod, setLocalPeriod] = useState(null);

  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date || null,
        end_date: localPeriod.end_date || null,
        label: localPeriod.label
      };
    }
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  const handlePeriodChange = useCallback((range) => {
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
    setCurrentPage(1);
  }, []);

  // Compute effective date range: month/year filter takes priority over fiscal period
  const effectiveDateRange = useMemo(() => {
    if (filters.month && filters.year) {
      const month = parseInt(filters.month);
      const year = parseInt(filters.year);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0); // last day of month
      return {
        start_date: formatDateToISO(start),
        end_date: formatDateToISO(end)
      };
    }
    if (filters.year && !filters.month) {
      const year = parseInt(filters.year);
      return {
        start_date: `${year}-01-01`,
        end_date: `${year}-12-31`
      };
    }
    return {
      start_date: activeDateRange.start_date,
      end_date: activeDateRange.end_date
    };
  }, [filters.month, filters.year, activeDateRange.start_date, activeDateRange.end_date]);

  // Determine if user explicitly chose "All Time" (local override with no dates)
  const isExplicitAllTime = localPeriod && !localPeriod.start_date && !localPeriod.end_date;

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 20, module: 'wholesale' };
      if (filters.status) params.status = filters.status;
      if (filters.category_id) params.category_id = filters.category_id;

      // Use effective date range (month/year filter or fiscal period)
      if (effectiveDateRange.start_date) params.start_date = effectiveDateRange.start_date;
      if (effectiveDateRange.end_date) params.end_date = effectiveDateRange.end_date;

      // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
      if (isExplicitAllTime && !effectiveDateRange.start_date && !effectiveDateRange.end_date) {
        params.period_type = 'forever';
      }

      const response = await getExpenses(params);
      setExpenses(response.data || []);
      setPagination(
        response.pagination || {
          page: currentPage,
          limit: 20,
          total: response.data?.length || 0,
          totalPages: 1
        }
      );
    } catch (error) {
      toast.error('Failed to load expenses');
      console.error(error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getExpenseCategories({ active_only: true });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
  }, [
    filters.status,
    filters.category_id,
    currentPage,
    effectiveDateRange.start_date,
    effectiveDateRange.end_date,
    isExplicitAllTime
  ]);

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this expense?')) {
      return;
    }

    try {
      await approveExpense(id);
      toast.success('Expense approved successfully');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve expense');
    }
  };

  const handleReject = (expense) => {
    // If called from Detail Modal, we might want to close it or keep it open?
    // Let's standard usage: setSelectedExpense is already set if we are in detail modal,
    // but the rejection modal uses the same state.
    // We can just open the Rejection Modal.
    // However, the Rejection Modal uses `selectedExpense` state.
    // And the Detail Modal also uses `selectedExpense` state.
    // So if I open Rejection Modal, I should probably close Detail Modal or ensure they don't conflict.
    // Better workflow: Open Rejection Modal on top of Detail Modal?
    // Or close Detail Modal. Let's close Detail Modal for now to avoid z-index overlap issues unless handled.
    setShowDetailModal(false);
    setSelectedExpense(expense);
    setShowRejectModal(true);
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await rejectExpense(selectedExpense.id, rejectionReason);
      toast.success('Expense rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject expense');
    }
  };

  const handlePostToGL = async (id) => {
    if (
      !window.confirm(
        'Are you sure you want to post this expense to the General Ledger? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await postExpenseToGL(id);
      toast.success('Expense posted to GL successfully');
      fetchExpenses();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to post expense to GL');
    }
  };

  const handleDelete = async (id, expenseNumber) => {
    if (!window.confirm(`Are you sure you want to delete expense ${expenseNumber}?`)) {
      return;
    }

    try {
      await deleteExpense(id);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete expense');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: FileText },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      posted: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}
      >
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (initialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading expenses...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Wholesale Expenses</h1>
            <p className="text-gray-600 mt-1">
              {isAdmin
                ? 'Review and approve wholesale expense submissions'
                : 'Manage your wholesale expense submissions'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowReportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText size={18} />
              Generate Report
            </button>
            <button
              onClick={() => {
                setEditingExpenseId(null);
                setShowExpenseModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 transition-colors"
              style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
            >
              <Plus size={20} />
              New Expense
            </button>
          </div>
        </div>
      </div>

      {/* Fiscal Period Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <p className="text-sm text-gray-600">
                Period:{' '}
                <span className="font-medium text-gray-900">
                  {filters.month || filters.year
                    ? `${filters.month ? new Date(2000, parseInt(filters.month) - 1).toLocaleString('en-US', { month: 'long' }) : 'All Months'} ${filters.year || ''}`
                    : localPeriod
                      ? activeDateRange.label
                      : `Global (${activeDateRange.label})`}
                </span>
                {localPeriod && !filters.month && !filters.year && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    Local Override
                  </span>
                )}
                {(filters.month || filters.year) && (
                  <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    Month/Year Filter
                  </span>
                )}
              </p>
            </div>
            <PeriodFilter
              onChange={handlePeriodChange}
              showLabel={true}
              compact={false}
              storageKey="wholesale_expenses"
            />
          </div>
        </div>

        {/* Month/Year & Status & Category Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={filters.month}
              onChange={(e) => {
                setFilters({ ...filters, month: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) => {
                setFilters({ ...filters, year: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="">All Years</option>
              {(() => {
                const currentYear = new Date().getFullYear();
                const years = [];
                for (let y = currentYear; y >= currentYear - 5; y--) {
                  years.push(
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                }
                return years;
              })()}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="posted">Posted</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category_id}
              onChange={(e) => {
                setFilters({ ...filters, category_id: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ status: '', category_id: '', search: '', month: '', year: '' });
                setLocalPeriod(null);
                setCurrentPage(1);
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expense #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linked Order
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                      Loading expenses...
                    </div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No wholesale expenses found.{' '}
                    {!isAdmin && 'Create your first expense to get started.'}
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr
                    key={expense.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setSelectedExpense(expense);
                      setShowDetailModal(true);
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {expense.expense_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(expense.expense_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{expense.category_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 line-clamp-2">
                        {expense.description}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm font-medium text-gray-900">
                        <DollarSign size={14} className="mr-1" />
                        {formatCurrency(expense.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{expense.submitted_by_name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(expense.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {expense.wholesale_order_id ? (
                        <div className="inline-flex items-center gap-1 text-sm text-emerald-600">
                          <Link2 size={14} />
                          {expense.order_number || `Order #${expense.wholesale_order_id}`}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setCurrentPage(page)}
          itemLabel="expenses"
        />
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Reject Expense</h2>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this expense:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              rows="4"
              placeholder="Enter rejection reason..."
              required
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={submitRejection}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Reject Expense
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedExpense(null);
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense Form Modal */}
      <ExpenseFormModal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          setEditingExpenseId(null);
        }}
        expenseId={editingExpenseId}
        onSuccess={fetchExpenses}
        module="wholesale"
      />

      {/* Expense Detail Modal */}
      <ExpenseDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onEdit={(expense) => {
          setEditingExpenseId(expense.id);
          setShowExpenseModal(true);
          setShowDetailModal(false);
        }}
        onApprove={(id) => {
          handleApprove(id);
          setShowDetailModal(false);
        }}
        onReject={handleReject}
        onDelete={(id, number) => {
          handleDelete(id, number);
          setShowDetailModal(false);
        }}
        onPostToGL={handlePostToGL}
        isAdmin={isAdmin}
        user={user}
      />

      {/* Expense Report Modal */}
      <ExpenseReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        filters={filters}
        activeDateRange={effectiveDateRange}
        expenseCount={pagination.total}
        isAllTime={isExplicitAllTime}
      />
    </div>
  );
};

export default WholesaleExpensesPage;
